import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, RefreshCw, FileText, Clock, Loader2, Ticket as TicketIcon, Upload, X, Download, CheckCircle2, AlertCircle, QrCode, Camera, CameraOff, Trash2 } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { openPdf } from "@/lib/pdf";

const EMPTY = {
  showtime_id: "",
  movie_title: "",
  date: new Date().toISOString().slice(0, 10),
  start_time: "20:00",
  hall: "Saal 1",
  row: "A",
  seat: "1",
  price: 8.0,
  ticket_type: "Standard",
  guest_name: "Notfall-Gast",
  notfall_ticket_no: "",
  cashier_note: "",
  payment_method: "bar",
};

export default function NotfallRebook() {
  const [params, setParams] = useSearchParams();
  const preNr = params.get("nr") || "";
  const [form, setForm] = useState({ ...EMPTY, notfall_ticket_no: preNr });
  const [showtimes, setShowtimes] = useState([]);
  const [recent, setRecent] = useState([]);
  const [blanks, setBlanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [scanNo, setScanNo] = useState("");

  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const loadShowtimes = async () => {
    try {
      const { data } = await api.get("/showtimes?upcoming_only=false");
      const rows = Array.isArray(data) ? data.slice(0, 200) : [];
      setShowtimes(rows);
    } catch { setShowtimes([]); }
  };
  const loadRecent = async () => {
    setLoadingRecent(true);
    try {
      const { data } = await api.get("/admin/notfall-rebook/recent?limit=15");
      setRecent(data.orders || []);
    } catch { setRecent([]); } finally { setLoadingRecent(false); }
  };
  const loadBlanks = async () => {
    try {
      const { data } = await api.get("/admin/notfall-blanks?status=issued&limit=50");
      setBlanks(data.blanks || []);
    } catch { setBlanks([]); }
  };
  useEffect(() => { loadShowtimes(); loadRecent(); loadBlanks(); }, []);

  // Bei URL ?nr=NF-...: Sofort per Scan-Endpoint prüfen
  useEffect(() => {
    if (!preNr) return;
    (async () => {
      try {
        const { data } = await api.get(`/notfall-blanks/lookup/${encodeURIComponent(preNr)}`);
        if (data?.blank?.status === "used") {
          toast.warning(`Ticket ${preNr} wurde bereits nachgebucht (${data.used_order?.order_number || "?"})`);
        } else {
          toast.success(`Notfall-Nr. ${preNr} geladen — bitte Daten ergänzen`);
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || `Notfall-Nr. ${preNr} nicht gefunden`);
      }
    })();
  }, [preNr]);

  const pickShowtime = (id) => {
    const st = showtimes.find((s) => s.id === id);
    if (!st) { setForm((f) => ({ ...f, showtime_id: "" })); return; }
    setForm((f) => ({
      ...f,
      showtime_id: id,
      movie_title: st.movie?.title_de || st.movie_title || "",
      date: st.date || f.date,
      start_time: st.start_time || f.start_time,
      hall: st.hall || f.hall,
    }));
  };

  const applyBlankNumber = async (nr) => {
    const clean = (nr || "").trim();
    if (!clean) return;
    try {
      const { data } = await api.get(`/notfall-blanks/lookup/${encodeURIComponent(clean)}`);
      if (data?.blank?.status === "used") {
        toast.warning(`${clean} wurde bereits nachgebucht (${data.used_order?.order_number || "?"})`);
        return;
      }
      if (data?.blank?.status === "voided") {
        toast.error(`${clean} wurde als ungültig markiert und kann nicht nachgebucht werden`);
        return;
      }
      setForm((f) => ({ ...f, notfall_ticket_no: clean }));
      setParams({ nr: clean }, { replace: true });
      toast.success(`Notfall-Nr. ${clean} übernommen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nummer nicht gefunden");
    }
  };

  // --- Kamera-QR-Scanner ---
  const [camOpen, setCamOpen] = useState(false);
  const scanRef = useRef(null);
  const camDivId = "nfr-cam-region";

  const stopCam = async () => {
    try { await scanRef.current?.stop(); await scanRef.current?.clear(); } catch { /* ignore */ }
    scanRef.current = null;
    setCamOpen(false);
  };

  const startCam = async () => {
    setCamOpen(true);
    setTimeout(async () => {
      try {
        const q = new Html5Qrcode(camDivId);
        scanRef.current = q;
        await q.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: 260 },
          async (decoded) => {
            // Extract NF-… aus URL oder direktem Text
            let nr = decoded;
            try {
              const u = new URL(decoded);
              const parts = u.pathname.split("/").filter(Boolean);
              nr = parts[parts.length - 1] || decoded;
            } catch { /* not a URL */ }
            await q.pause(true);
            await applyBlankNumber(nr);
            await stopCam();
          },
          () => {},
        );
      } catch (e) {
        toast.error("Kamera nicht verfügbar. Bitte Nummer manuell eingeben.");
        setCamOpen(false);
      }
    }, 100);
  };

  useEffect(() => () => { stopCam(); }, []);

  // --- Blank stornieren ---
  const voidBlank = async (nr) => {
    if (!window.confirm(`Notfall-Blank ${nr} als ungültig markieren?\n(Verlorenes/beschädigtes Ticket — wird aus offener Liste entfernt)`)) return;
    const reason = window.prompt("Grund (optional):", "") || "";
    try {
      await api.post(`/admin/notfall-blanks/${encodeURIComponent(nr)}/void`, { reason });
      toast.success(`${nr} als ungültig markiert`);
      loadBlanks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Storno fehlgeschlagen");
    }
  };

  // --- Alle heute offenen Blanks sammelstornieren ---
  const bulkVoidToday = async () => {
    const today = new Date().toISOString().slice(0, 10);
    const todayCount = blanks.filter((b) => (b.number || "").startsWith(`NF-${today.replace(/-/g, "")}-`)).length;
    if (todayCount === 0) {
      toast.info("Heute sind keine offenen Blanks zum Stornieren");
      return;
    }
    if (!window.confirm(`Alle ${todayCount} offenen Notfall-Blanks von heute (${today}) stornieren?\n(Bereits nachgebuchte bleiben unberührt)`)) return;
    const reason = window.prompt("Grund (optional):", "Tagesabschluss — unbenutzte Papier-Tickets") || "";
    try {
      const { data } = await api.post(`/admin/notfall-blanks/bulk-void-today`, { date: today, reason });
      toast.success(`${data.voided_count} Blanks storniert`);
      loadBlanks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sammelstorno fehlgeschlagen");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.movie_title && !form.showtime_id) {
      toast.error("Bitte Vorstellung wählen oder Filmtitel eintragen");
      return;
    }
    setBusy(true);
    try {
      const body = { ...form, price: Number(form.price) || 0 };
      const { data } = await api.post("/admin/notfall-rebook", body);
      toast.success(`Notfall-Ticket #${data.ticket.ticket_number} nachgebucht`);
      openPdf(`/tickets/${data.ticket.id}/pdf`);
      setForm({ ...EMPTY });
      setParams({}, { replace: true });
      loadRecent();
      loadBlanks();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nachbuchung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="notfall-rebook-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Notfall · Rückerfassung</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3">
            <AlertTriangle size={28} className="text-[#EE9CA1]" /> Handschriftliche Tickets nachbuchen
          </h1>
          <div className="mt-2 text-xs text-[#A8A398] max-w-2xl">
            Nach einem Notfall-Verkauf (Papier-Ticket wegen Ausfall): Daten hier eintragen — die Bestellung wandert in Statistik und Kassenbuch, sodass alles stimmt. Bei bekannter Vorstellung einfach auswählen, sonst als Freitext.
          </div>
        </div>
        <button onClick={() => setBulkOpen(true)} data-testid="nfr-bulk-open"
          className="inline-flex items-center gap-2 self-start px-4 py-2 border border-[#EEDC82]/60 text-[#EEDC82] rounded text-[10px] uppercase tracking-[0.32em] hover:bg-[#EEDC82] hover:text-black">
          <Upload size={12} /> CSV-Import
        </button>
      </div>

      {/* QR-Scan / Blank-Nummer eingeben */}
      <div className="card-lux p-4 bg-gradient-to-br from-[#8B7328]/10 to-transparent" data-testid="nfr-scan-box">
        <div className="flex items-center gap-3 flex-wrap">
          <QrCode size={20} className="text-[#EEDC82] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#EEDC82]">Notfall-QR scannen · Nr. eingeben</div>
            <div className="text-xs text-[#A8A398] mt-0.5">Kamera-Scan, Nummer manuell oder aus Liste offener Blanks — Formular wird auto-gefüllt.</div>
          </div>
          <div className="flex gap-2 items-center w-full sm:w-auto flex-wrap">
            {!camOpen ? (
              <button onClick={startCam} data-testid="nfr-cam-start"
                className="px-3 py-2 bg-[#EEDC82] text-black rounded text-[10px] uppercase tracking-[0.32em] hover:bg-[#F5E9A3] flex items-center gap-1.5 shrink-0">
                <Camera size={13} /> Kamera
              </button>
            ) : (
              <button onClick={stopCam} data-testid="nfr-cam-stop"
                className="px-3 py-2 border border-[#EE9CA1]/60 text-[#EE9CA1] rounded text-[10px] uppercase tracking-[0.32em] flex items-center gap-1.5 shrink-0">
                <CameraOff size={13} /> Stopp
              </button>
            )}
            <input value={scanNo} onChange={(e) => setScanNo(e.target.value)}
              placeholder="NF-20260206-0001"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyBlankNumber(scanNo); setScanNo(""); } }}
              data-testid="nfr-scan-input"
              className="flex-1 bg-[#050505] border border-[#EEDC82]/40 rounded px-3 py-2 text-sm font-mono focus:border-[#EEDC82] outline-none min-w-[160px]" />
            <button onClick={() => { applyBlankNumber(scanNo); setScanNo(""); }}
              data-testid="nfr-scan-apply"
              className="shrink-0 px-4 py-2 bg-[#EEDC82] text-black rounded text-[10px] uppercase tracking-[0.32em] hover:bg-[#F5E9A3]">
              Übernehmen
            </button>
          </div>
        </div>
        {camOpen && (
          <div className="mt-3">
            <div id={camDivId} className="w-full max-w-xs mx-auto aspect-square bg-black rounded overflow-hidden" />
            <div className="text-center text-[10px] text-[#A8A398] mt-2 uppercase tracking-widest">QR auf Papier-Ticket vor Kamera halten …</div>
          </div>
        )}
        {blanks.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#EEDC82]/20">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="text-[9px] uppercase tracking-[0.3em] text-[#A8A398]">Offene Blanks ({blanks.length}) — Klick zum Übernehmen · Papierkorb-Icon storniert</div>
              <button onClick={bulkVoidToday} data-testid="nfr-bulk-void-today"
                title="Alle heute offenen Blanks auf einen Schlag stornieren"
                className="text-[9px] font-mono uppercase tracking-[0.28em] px-2.5 py-1 border border-[#EE9CA1]/50 text-[#EE9CA1] rounded hover:bg-[#7A1921] hover:text-white inline-flex items-center gap-1">
                <Trash2 size={10} /> Heute alle stornieren
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
              {blanks.slice(0, 30).map((b) => (
                <div key={b.id} className="inline-flex items-stretch border border-[#8B7328]/50 rounded overflow-hidden">
                  <button onClick={() => applyBlankNumber(b.number)}
                    data-testid={`nfr-blank-${b.number}`}
                    className="text-[10px] font-mono px-2 py-1 text-[#EEDC82] hover:bg-[#EEDC82] hover:text-black">
                    {b.number}
                  </button>
                  <button onClick={() => voidBlank(b.number)}
                    data-testid={`nfr-blank-void-${b.number}`}
                    title={`${b.number} als ungültig markieren`}
                    className="px-1.5 border-l border-[#8B7328]/50 text-[#EE9CA1] hover:bg-[#7A1921] hover:text-white">
                    <Trash2 size={10} />
                  </button>
                </div>
              ))}
              {blanks.length > 30 && <span className="text-[10px] text-[#575148] self-center">… +{blanks.length - 30} weitere</span>}
            </div>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="card-lux p-5 space-y-4" data-testid="notfall-rebook-form">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Vorstellung (optional)</label>
            <select value={form.showtime_id} onChange={(e) => pickShowtime(e.target.value)}
              data-testid="nfr-showtime"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EE9CA1] outline-none">
              <option value="">— Freitext benutzen —</option>
              {showtimes.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.movie?.title_de || s.movie_title || "?")} · {s.date} · {s.start_time} · {s.hall}
                </option>
              ))}
            </select>
            <div className="text-[10px] text-[#575148] mt-1">Wenn du eine Vorstellung wählst, füllen Film, Datum, Zeit, Saal automatisch.</div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <F label="Filmtitel" v={form.movie_title} on={upd("movie_title")} testid="nfr-movie" placeholder="z.B. Interstellar" />
            <F label="Notfall-Ticket-Nr." v={form.notfall_ticket_no} on={upd("notfall_ticket_no")} testid="nfr-no" placeholder="MH-2026-042" />
            <F label="Datum" v={form.date} on={upd("date")} type="date" testid="nfr-date" />
            <F label="Uhrzeit" v={form.start_time} on={upd("start_time")} type="time" testid="nfr-time" />
            <F label="Saal" v={form.hall} on={upd("hall")} testid="nfr-hall" placeholder="Saal 1" />
            <F label="Sitz-Typ" v={form.ticket_type} on={upd("ticket_type")} testid="nfr-type" placeholder="Standard" />
            <F label="Reihe" v={form.row} on={upd("row")} testid="nfr-row" />
            <F label="Platz" v={form.seat} on={upd("seat")} testid="nfr-seat" />
            <F label="Preis (€)" v={form.price} on={(v) => upd("price")(v)} type="number" step="0.01" testid="nfr-price" />
            <div>
              <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Zahlweise</label>
              <select value={form.payment_method} onChange={(e) => upd("payment_method")(e.target.value)}
                data-testid="nfr-pay"
                className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EE9CA1] outline-none">
                <option value="bar">Bar</option>
                <option value="karte">Karte</option>
                <option value="ueberweisung">Überweisung</option>
                <option value="gutschein">Gutschein</option>
              </select>
            </div>
          </div>

          <F label="Gastname (optional)" v={form.guest_name} on={upd("guest_name")} testid="nfr-guest" />

          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Kassierer-Notiz</label>
            <textarea value={form.cashier_note} onChange={(e) => upd("cashier_note")(e.target.value)}
              rows={2} data-testid="nfr-note"
              placeholder="z.B. Grund: Stromausfall, Kassierer: Anna"
              className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EE9CA1] outline-none resize-none" />
          </div>

          <button type="submit" disabled={busy} data-testid="nfr-submit"
            className="w-full py-3 bg-[#EE9CA1] hover:bg-[#F5B5B9] text-black rounded text-[11px] uppercase tracking-[0.4em] inline-flex items-center justify-center gap-2 disabled:opacity-40">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Buche nach …</> : <><TicketIcon size={14} /> Ticket nachbuchen + PDF öffnen</>}
          </button>
        </form>

        <div className="card-lux p-5" data-testid="notfall-rebook-recent">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Letzte Nachbuchungen</div>
              <div className="text-sm text-[#F5F0E6] mt-0.5">{recent.length} Einträge im System</div>
            </div>
            <button onClick={loadRecent} className="text-[10px] uppercase tracking-[0.3em] px-3 py-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37] inline-flex items-center gap-1">
              <RefreshCw size={11} /> Aktualisieren
            </button>
          </div>
          {loadingRecent ? (
            <div className="text-center py-10 text-[#575148]"><Loader2 size={16} className="animate-spin inline" /></div>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 text-[#575148] text-xs italic">Noch keine Notfall-Nachbuchungen.</div>
          ) : (
            <div className="space-y-2">
              {recent.map((o) => (
                <div key={o.id} className="bg-black/30 border border-[#1A1A1A] rounded p-3 flex items-center justify-between gap-3"
                  data-testid={`nfr-recent-${o.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-xs text-[#EEDC82]">{o.order_number}</span>
                      <span className="text-sm text-[#F5F0E6] truncate">{o.movie_title || "—"}</span>
                    </div>
                    <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-[#A8A398] flex items-center gap-2 flex-wrap">
                      <Clock size={10} /> {o.items?.[0]?.hall || o.hall || "—"} · Reihe {o.items?.[0]?.row} · Platz {o.items?.[0]?.seat}
                      <span>·</span>
                      <span className="font-mono">{Number(o.total || 0).toFixed(2)} €</span>
                      {o.notfall_ticket_no && <><span>·</span><span className="font-mono text-[#EE9CA1]">{o.notfall_ticket_no}</span></>}
                    </div>
                    <div className="text-[9px] font-mono text-[#575148] mt-0.5">
                      {o.created_at ? new Date(o.created_at).toLocaleString("de-DE") : ""}
                    </div>
                  </div>
                  {o.ticket_ids?.[0] && (
                    <button onClick={() => openPdf(`/tickets/${o.ticket_ids[0]}/pdf`)}
                      title="Ticket-PDF öffnen"
                      className="text-xs px-2 py-1.5 border border-[#EEDC82]/40 rounded hover:bg-[#EEDC82]/10 text-[#EEDC82]">
                      <FileText size={12} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {bulkOpen && (
        <BulkImportDialog
          onClose={() => setBulkOpen(false)}
          onDone={() => loadRecent()}
        />
      )}
    </div>
  );
}

function F({ label, v, on, type = "text", step, testid, placeholder }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type={type} value={v} onChange={(e) => on(e.target.value)}
        step={step} data-testid={testid} placeholder={placeholder}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 focus:border-[#EE9CA1] outline-none" />
    </div>
  );
}

// ---- CSV Bulk Import -----------------------------------------------------
const CSV_HEADERS = ["movie_title", "date", "start_time", "hall", "row", "seat", "price", "notfall_ticket_no", "payment_method", "cashier_note", "guest_name"];
const CSV_TEMPLATE =
  CSV_HEADERS.join(";") + "\n" +
  "Interstellar;2026-02-15;20:00;Saal 1;C;07;12.50;MH-NF-001;bar;Stromausfall;Familie Müller\n" +
  "Dune Part 2;2026-02-15;20:00;Saal 1;C;08;12.50;MH-NF-002;karte;;Peter Schmidt\n";

function parseCsv(text) {
  const raw = text.replace(/\r\n?/g, "\n").trim();
  if (!raw) return { rows: [], errors: [{ line: 0, msg: "Datei ist leer" }] };
  const lines = raw.split("\n");
  const first = lines[0].split(/[;,]/).map((s) => s.trim().toLowerCase());
  const hasHeader = first.some((h) => CSV_HEADERS.includes(h));
  const header = hasHeader ? first : CSV_HEADERS;
  const startAt = hasHeader ? 1 : 0;
  const rows = []; const errors = [];
  for (let i = startAt; i < lines.length; i++) {
    const line = lines[i].trim(); if (!line) continue;
    const cols = line.split(/[;,]/).map((c) => c.trim());
    const row = {};
    header.forEach((h, idx) => { if (CSV_HEADERS.includes(h)) row[h] = cols[idx] ?? ""; });
    if (!row.movie_title) { errors.push({ line: i + 1, msg: "movie_title fehlt" }); continue; }
    row.price = parseFloat(String(row.price || 0).replace(",", ".")) || 0;
    row.payment_method = row.payment_method || "bar";
    row.hall = row.hall || "Saal 1";
    row.row = row.row || "A";
    row.seat = row.seat || "1";
    row.guest_name = row.guest_name || "Notfall-Gast";
    rows.push(row);
  }
  return { rows, errors };
}

function BulkImportDialog({ onClose, onDone }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const parsed = useMemo(() => parseCsv(text), [text]);

  const onFile = async (f) => {
    if (!f) return;
    const t = await f.text();
    setText(t);
  };
  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "notfall-import-vorlage.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };
  const importNow = async () => {
    if (!parsed.rows.length) { toast.error("Keine gültigen Zeilen zum Importieren"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/notfall-rebook/bulk", { rows: parsed.rows });
      setResult(data);
      toast.success(`${data.imported} von ${parsed.rows.length} Tickets nachgebucht`);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="nfr-bulk-dialog">
        <div className="px-6 py-4 border-b border-[#1A1A1A] flex items-center gap-3">
          <Upload size={20} className="text-[#EEDC82]" />
          <div>
            <div className="font-heading text-xl text-[#EEDC82]">Notfall-Tickets · CSV-Import</div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-0.5">Bis zu 500 Tickets auf einmal nachbuchen</div>
          </div>
          <button onClick={onClose} className="ml-auto"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={20} className="text-[#8FBC8F]" />
                <div className="text-lg">
                  <span className="text-[#8FBC8F]">{result.imported}</span>
                  <span className="text-[#A8A398]"> importiert</span>
                  {result.failed > 0 && (
                    <>
                      <span className="text-[#575148]"> · </span>
                      <span className="text-[#EE9CA1]">{result.failed}</span>
                      <span className="text-[#A8A398]"> fehlgeschlagen</span>
                    </>
                  )}
                </div>
              </div>
              {result.results?.length > 0 && (
                <div className="border border-[#1A1A1A] rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-black/40 sticky top-0">
                      <tr className="text-left text-[#A8A398]">
                        <th className="p-2 uppercase tracking-widest text-[9px]">Order-Nr</th>
                        <th className="p-2 uppercase tracking-widest text-[9px]">Ticket-Nr</th>
                        <th className="p-2 uppercase tracking-widest text-[9px]">Film</th>
                        <th className="p-2 uppercase tracking-widest text-[9px] text-right">Preis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.results.map((r) => (
                        <tr key={`${r.order_number}-${r.ticket_number}`} className="border-t border-[#141414] font-mono">
                          <td className="p-2 text-[#EEDC82]">{r.order_number}</td>
                          <td className="p-2 text-[#EE9CA1]">{r.ticket_number}</td>
                          <td className="p-2 text-[#F5F0E6]">{r.movie_title}</td>
                          <td className="p-2 text-right">{Number(r.total || 0).toFixed(2)} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {result.errors?.length > 0 && (
                <div className="border border-[#EE9CA1]/40 rounded-lg p-3 text-xs space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#EE9CA1] mb-1">Fehler</div>
                  {result.errors.map((e, i) => (
                    <div key={i} className="text-[#EE9CA1]/80">Zeile {e.index + 1}: {String(e.detail)}</div>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t border-[#1A1A1A]">
                <button onClick={() => { setText(""); setResult(null); }}
                  className="px-4 py-2 text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
                  Weiteren Import starten
                </button>
                <button onClick={onClose}
                  data-testid="nfr-bulk-close"
                  className="px-4 py-2 text-[10px] uppercase tracking-widest bg-[#D4AF37] text-black rounded hover:bg-[#EEDC82]">
                  Fertig
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 items-center">
                <button onClick={() => fileRef.current?.click()} data-testid="nfr-bulk-file"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#EEDC82]/50 text-[#EEDC82] rounded text-[10px] uppercase tracking-widest hover:bg-[#EEDC82] hover:text-black">
                  <Upload size={12} /> CSV-Datei wählen
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden"
                  onChange={(e) => onFile(e.target.files?.[0])} />
                <button onClick={downloadTemplate} data-testid="nfr-bulk-template"
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[#2A2A2A] rounded text-[10px] uppercase tracking-widest text-[#A8A398] hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  <Download size={12} /> Vorlage laden
                </button>
                <div className="text-[10px] text-[#575148]">
                  Trennzeichen: <span className="text-[#EEDC82] font-mono">;</span> oder <span className="text-[#EEDC82] font-mono">,</span> · Header wird automatisch erkannt
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">CSV-Inhalt (auch einfügbar per Copy-Paste)</label>
                <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10}
                  data-testid="nfr-bulk-textarea"
                  placeholder={CSV_TEMPLATE}
                  className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-xs focus:border-[#EE9CA1] outline-none" />
              </div>
              <div className="flex items-center gap-3 text-xs">
                <div className="text-[#8FBC8F]">
                  <CheckCircle2 size={12} className="inline mr-1" /> {parsed.rows.length} gültige Zeile{parsed.rows.length === 1 ? "" : "n"}
                </div>
                {parsed.errors.length > 0 && (
                  <div className="text-[#EE9CA1]">
                    <AlertCircle size={12} className="inline mr-1" /> {parsed.errors.length} Fehler
                  </div>
                )}
              </div>
              {parsed.rows.length > 0 && (
                <div className="border border-[#1A1A1A] rounded overflow-hidden max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-black/40 sticky top-0">
                      <tr className="text-left text-[#A8A398]">
                        <th className="p-2 text-[9px] uppercase tracking-widest">Film</th>
                        <th className="p-2 text-[9px] uppercase tracking-widest">Datum · Zeit</th>
                        <th className="p-2 text-[9px] uppercase tracking-widest">Saal · Sitz</th>
                        <th className="p-2 text-[9px] uppercase tracking-widest text-right">Preis</th>
                        <th className="p-2 text-[9px] uppercase tracking-widest">NF-Nr</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 20).map((r, i) => (
                        <tr key={i} className="border-t border-[#141414]">
                          <td className="p-2 text-[#F5F0E6]">{r.movie_title}</td>
                          <td className="p-2 font-mono">{r.date} · {r.start_time}</td>
                          <td className="p-2 font-mono">{r.hall} · {r.row}{r.seat}</td>
                          <td className="p-2 text-right font-mono">{Number(r.price).toFixed(2)} €</td>
                          <td className="p-2 font-mono text-[#EE9CA1]">{r.notfall_ticket_no || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parsed.rows.length > 20 && (
                    <div className="p-2 text-[10px] text-[#575148] text-center bg-black/20">
                      … und {parsed.rows.length - 20} weitere Zeilen
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2 pt-2 border-t border-[#1A1A1A]">
                <button onClick={onClose}
                  className="px-4 py-2 text-[10px] uppercase tracking-widest border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
                  Abbrechen
                </button>
                <button onClick={importNow} disabled={busy || parsed.rows.length === 0}
                  data-testid="nfr-bulk-import"
                  className="ml-auto inline-flex items-center gap-2 px-6 py-2 text-[10px] uppercase tracking-widest bg-[#EE9CA1] hover:bg-[#F5B5B9] text-black rounded disabled:opacity-40">
                  {busy ? <><Loader2 size={12} className="animate-spin" /> Importiere …</> : <><Upload size={12} /> {parsed.rows.length} Tickets nachbuchen</>}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
